import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Button, TextField, InputAdornment, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, MenuItem, Select, FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack, Search, PictureAsPdf, Payment, Edit, Delete,
  Receipt, Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function Cobros() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cobros, setCobros] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [detalleCobro, setDetalleCobro] = useState(null);
  const [openDetalle, setOpenDetalle] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openEliminar, setOpenEliminar] = useState(false);
  const [cobroEditar, setCobroEditar] = useState(null);
  const [cobroEliminar, setCobroEliminar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargarCobros(); }, []);

  const cargarCobros = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-cobrar/cobros`, { headers: getHeaders() });
      const data = res.data?.data || [];
      setCobros(data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
    } catch (err) {
      console.error('Error cargando cobros:', err);
    } finally { setLoading(false); }
  };

  const cobrosFiltrados = cobros.filter(c => {
    if (!filtro) return true;
    const f = filtro.toLowerCase();
    return (c.cliente_nombre || '').toLowerCase().includes(f) ||
      (c.cliente_ruc || '').includes(f) ||
      (c.numero_recibo || '').includes(f) ||
      (c.referencia || '').toLowerCase().includes(f);
  });

  const formatFecha = (fecha) => {
    if (!fecha) return '—';
    return new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const formatMonto = (monto) => `$${parseFloat(monto || 0).toFixed(2)}`;

  const formaPagoLabel = {
    EFECTIVO: { label: 'Efectivo', color: 'success' },
    TRANSFERENCIA: { label: 'Transferencia', color: 'info' },
    CHEQUE: { label: 'Cheque', color: 'warning' },
    TARJETA: { label: 'Tarjeta', color: 'secondary' },
    DEPOSITO: { label: 'Depósito', color: 'info' },
  };

  // ===== EDITAR =====
  const abrirEditar = (cobro) => {
    setCobroEditar({ ...cobro });
    setOpenEditar(true);
  };

  const guardarEdicion = async () => {
    if (!cobroEditar) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/cuentas-cobrar/cobros/${cobroEditar.id}`, {
        monto: parseFloat(cobroEditar.monto),
        fecha: cobroEditar.fecha,
        forma_pago: cobroEditar.forma_pago,
        referencia: cobroEditar.referencia,
        observaciones: cobroEditar.observaciones,
        cuenta_contable: cobroEditar.cuenta_contable,
      }, { headers: getHeaders() });
      setMensaje({ tipo: 'success', texto: 'Cobro actualizado correctamente. Contabilidad ajustada.' });
      setOpenEditar(false);
      cargarCobros();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.message || 'Error al actualizar cobro' });
    } finally { setSaving(false); }
  };

  // ===== ELIMINAR =====
  const abrirEliminar = (cobro) => {
    setCobroEliminar(cobro);
    setOpenEliminar(true);
  };

  const confirmarEliminar = async () => {
    if (!cobroEliminar) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/cuentas-cobrar/cobros/${cobroEliminar.id}`, { headers: getHeaders() });
      setMensaje({ tipo: 'success', texto: 'Cobro eliminado. Asiento contable y saldo de caja/banco reversados.' });
      setOpenEliminar(false);
      cargarCobros();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.message || 'Error al eliminar cobro' });
    } finally { setSaving(false); }
  };

  const imprimirComprobante = async (cobro) => {
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-cobrar/cobros/${cobro.id}/pdf`,
        { headers: getHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { alert('Error al generar comprobante PDF'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/cuentas-cobrar')}><ArrowBack /></IconButton>
        <Receipt color="primary" />
        <Typography variant="h5" fontWeight={700}>Cobros Registrados</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Payment />}
          onClick={() => navigate('/cuentas-cobrar/registrar-cobro')}>
          Nuevo Cobro
        </Button>
      </Box>

      {mensaje && (
        <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>
          {mensaje.texto}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField fullWidth size="small" placeholder="Buscar por cliente, RUC, recibo o referencia..."
          value={filtro} onChange={(e) => setFiltro(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Paper>

      {cobros.length === 0 ? (
        <Alert severity="info">No hay cobros registrados aún.</Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>No. Recibo</strong></TableCell>
                  <TableCell><strong>Cliente</strong></TableCell>
                  <TableCell align="right"><strong>Cobrado</strong></TableCell>
                  <TableCell align="right"><strong>Ret. IR</strong></TableCell>
                  <TableCell align="right"><strong>Ret. IVA</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell><strong>Forma Pago</strong></TableCell>
                  <TableCell><strong>Referencia</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cobrosFiltrados.map(c => {
                  const fp = formaPagoLabel[c.forma_pago] || { label: c.forma_pago, color: 'default' };
                  const retFuente = parseFloat(c.ret_fuente_monto || 0);
                  const retIVA = parseFloat(c.ret_iva_monto || 0);
                  const totalCancelado = parseFloat(c.monto || 0) + retFuente + retIVA;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{formatFecha(c.fecha)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.numero_recibo || c.id?.substring(0, 8)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{c.cliente_nombre || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.cliente_ruc || ''}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{formatMonto(c.monto)}</TableCell>
                      <TableCell align="right" sx={{ color: retFuente > 0 ? '#7b1fa2' : 'text.disabled', fontSize: '0.8rem' }}>
                        {retFuente > 0 ? formatMonto(retFuente) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: retIVA > 0 ? '#7b1fa2' : 'text.disabled', fontSize: '0.8rem' }}>
                        {retIVA > 0 ? formatMonto(retIVA) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMonto(totalCancelado)}</TableCell>
                      <TableCell><Chip label={fp.label} color={fp.color} size="small" /></TableCell>
                      <TableCell>{c.referencia || '—'}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => abrirEditar(c)}><Edit /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => abrirEliminar(c)}><Delete /></IconButton></Tooltip>
                        <Tooltip title="PDF"><IconButton size="small" onClick={() => imprimirComprobante(c)}><PictureAsPdf /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f9f9f9' }}>
            <Typography variant="body2" color="text.secondary">{cobrosFiltrados.length} cobro(s)</Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="body2">
                Cobrado: <strong style={{ color: '#388e3c' }}>${cobrosFiltrados.reduce((s, c) => s + parseFloat(c.monto || 0), 0).toFixed(2)}</strong>
              </Typography>
              {cobrosFiltrados.some(c => parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0) > 0) && (
                <Typography variant="body2">
                  Ret. clientes: <strong style={{ color: '#7b1fa2' }}>
                    ${cobrosFiltrados.reduce((s, c) => s + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0), 0).toFixed(2)}
                  </strong>
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700}>
                Total CxC cancelado: ${cobrosFiltrados.reduce((s, c) =>
                  s + parseFloat(c.monto || 0) + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0), 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Dialog EDITAR */}
      <Dialog open={openEditar} onClose={() => setOpenEditar(false)} maxWidth="sm" fullWidth>
        {cobroEditar && (
          <>
            <DialogTitle>✏️ Editar Cobro</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Monto" type="number" value={cobroEditar.monto}
                    onChange={(e) => setCobroEditar({ ...cobroEditar, monto: e.target.value })}
                    inputProps={{ step: '0.01', min: '0.01' }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Fecha" type="date" value={(cobroEditar.fecha || '').substring(0, 10)}
                    onChange={(e) => setCobroEditar({ ...cobroEditar, fecha: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Forma de Pago</InputLabel>
                    <Select value={cobroEditar.forma_pago || 'EFECTIVO'} label="Forma de Pago"
                      onChange={(e) => setCobroEditar({ ...cobroEditar, forma_pago: e.target.value })}>
                      <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                      <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                      <MenuItem value="CHEQUE">Cheque</MenuItem>
                      <MenuItem value="DEPOSITO">Depósito</MenuItem>
                      <MenuItem value="TARJETA">Tarjeta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Referencia" value={cobroEditar.referencia || ''}
                    onChange={(e) => setCobroEditar({ ...cobroEditar, referencia: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Observaciones" multiline rows={2} value={cobroEditar.observaciones || ''}
                    onChange={(e) => setCobroEditar({ ...cobroEditar, observaciones: e.target.value })} />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Si cambia el monto, se generará un asiento contable de ajuste automáticamente.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditar(false)}>Cancelar</Button>
              <Button variant="contained" onClick={guardarEdicion} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog ELIMINAR */}
      <Dialog open={openEliminar} onClose={() => setOpenEliminar(false)} maxWidth="xs" fullWidth>
        {cobroEliminar && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" /> Eliminar Cobro
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>¿Está seguro de eliminar este cobro?</Typography>
              <Paper sx={{ p: 2, bgcolor: '#fff3e0', mt: 1 }}>
                <Typography variant="body2"><strong>Cliente:</strong> {cobroEliminar.cliente_nombre}</Typography>
                <Typography variant="body2"><strong>Monto:</strong> {formatMonto(cobroEliminar.monto)}</Typography>
                <Typography variant="body2"><strong>Fecha:</strong> {formatFecha(cobroEliminar.fecha)}</Typography>
              </Paper>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Se reversará el asiento contable y se devolverá el saldo a la cuenta de origen (caja/banco).
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEliminar(false)}>Cancelar</Button>
              <Button variant="contained" color="error" onClick={confirmarEliminar} disabled={saving}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
